import { router } from "@inertiajs/react";
import { useState } from "react";

import ConfirmDialog from "@/components/ConfirmDialog";
import type { Column, PagyMeta, RowAction } from "@/components/DataTable";
import DataTable from "@/components/DataTable";
import FilterBar, { type FilterField } from "@/components/FilterBar";
import LinkButton from "@/components/LinkButton";
import { usePermissions } from "@/lib/permissions";
import { categories } from "@/routes";
import type { Category } from "@/types";

function exportUrl(path: string, filters: Record<string, string>): string {
  // Keep blank values: an explicit empty param (e.g. "status=") tells the
  // backend NOT to apply that filter's default.
  const qs = new URLSearchParams(Object.entries(filters)).toString();
  return qs ? `${path}?${qs}` : path;
}

const STATUS_LABELS: Record<string, string> = {
  active: "Activo",
  inactive: "Inactivo",
};

const COURSE_TYPE_LABELS: Record<string, string> = {
  retirement_course: "Curso de retiro",
  coexistence: "Convivencia",
  other: "Otro",
  yearly: "Curso Anual",
  jornada: "Jornada",
};

type Props = {
  data: Category[];
  pagy: PagyMeta;
  filters: Record<string, string>;
};

const COLUMNS: Column<Category>[] = [
  {
    header: "Categoria",
    cell: (c) =>
      c.course_type
        ? (COURSE_TYPE_LABELS[c.course_type] ?? c.course_type)
        : "-",
  },
  {
    header: "Tipo de curso",
    cell: (c) => c.name || "-",
  },
  {
    header: "Estado",
    cell: (c) => (c.status ? (STATUS_LABELS[c.status] ?? c.status) : "-"),
  },
];

const FILTER_FIELDS: FilterField[] = [
  {
    key: "name",
    label: "Tipo",
    type: "text",
    placeholder: "Buscar por tipo...",
  },
  {
    key: "status",
    label: "Estado",
    type: "select",
    options: [
      { label: "Todos", value: "" },
      { label: "Activo", value: "active" },
      { label: "Inactivo", value: "inactive" },
    ],
  },
];

export default function CategoriesIndex({ data: rows, pagy, filters }: Props) {
  const permissions = usePermissions();

  const [deleteTarget, setDeleteTarget] = useState<Category | null>(null);

  const actions: RowAction<Category>[] = [
    ...(permissions.canManageAdminOnlyRecords
      ? ([
          {
            label: "Editar",
            href: (c) => categories.edit(c.id).url,
            variant: "primary",
          },
        ] satisfies RowAction<Category>[])
      : []),
    ...(permissions.canManageAdminOnlyRecords
      ? ([
          {
            label: "Eliminar",
            onClick: (c) => setDeleteTarget(c),
            variant: "danger",
          },
        ] satisfies RowAction<Category>[])
      : []),
  ];

  function navigate(
    params: Record<string, string | number>,
    opts?: { replace?: boolean },
  ) {
    router.get(categories.index().url, params, {
      preserveScroll: true,
      replace: opts?.replace ?? false,
    });
  }

  return (
    <>
      <div className="w-full py-10">
        <div className="px-6 sm:px-8 lg:px-12">
          <div className="mb-6 flex items-center justify-between gap-4">
            <h1 className="font-sans text-3xl font-semibold text-ink">
              Tipos de curso
            </h1>

            {permissions.canManageAdminOnlyRecords && (
              <LinkButton
                href={categories.new().url}
                variant="primary"
                className="px-4 py-1.5 text-sm"
              >
                Nuevo
              </LinkButton>
            )}
          </div>

          <FilterBar
            fields={FILTER_FIELDS}
            values={filters}
            onChange={(newFilters) =>
              navigate({ ...newFilters, page: 1 }, { replace: true })
            }
          />
        </div>

        <DataTable
          columns={COLUMNS}
          rows={rows}
          keyExtractor={(c) => c.id}
          actions={actions}
          utilities={
            <a
              href={exportUrl("/categories/export_excel", filters)}
              className="font-sans text-sm font-light text-ink underline underline-offset-2 hover:text-steel"
            >
              Exportar Excel
            </a>
          }
          pagy={pagy}
          onPageChange={(page) => navigate({ ...filters, page })}
          emptyMessage="No hay tipos de curso registradas."
        />
      </div>

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Eliminar tipo de curso"
        message={`Se eliminará el tipo de curso ${deleteTarget?.name ?? ""}.`}
        confirmLabel="Eliminar"
        variant="danger"
        onConfirm={() => {
          if (deleteTarget) {
            router.delete(categories.destroy(deleteTarget.id).url, {
              preserveScroll: true,
            });
          }
          setDeleteTarget(null);
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
}
